/**
 * Sensitivity Analysis Section for Accountant Report View
 * Displays range of outcomes and key variables impact analysis
 */

import React from 'react';

interface SensitivityAnalysisSectionProps {
  finalClaimAmount: number;
  formatCurrency: (amount?: number) => string;
}

export default function SensitivityAnalysisSection({
  finalClaimAmount,
  formatCurrency
}: SensitivityAnalysisSectionProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <span className="text-2xl mr-2">📈</span>
        Sensitivity Analysis - Range of Outcomes
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Shows how the claim value changes under different assumptions. Essential for settlement negotiations and court testimony.
        Demonstrates the robustness of the calculation and provides confidence intervals.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-500">
          <p className="text-xs text-red-700 font-medium mb-1">CONSERVATIVE</p>
          <p className="text-sm text-red-800 mb-2 font-semibold">Worst Case Scenario</p>
          <p className="text-2xl font-bold text-red-900 mb-2">
            {formatCurrency(finalClaimAmount * 0.85)}
          </p>
          <div className="text-xs text-red-700 bg-red-100 rounded px-2 py-1">
            15% reduction in assumptions
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
          <p className="text-xs text-green-700 font-medium mb-1">BASE CASE</p>
          <p className="text-sm text-green-800 mb-2 font-semibold">Most Likely</p>
          <p className="text-2xl font-bold text-green-900 mb-2">
            {formatCurrency(finalClaimAmount)}
          </p>
          <div className="text-xs text-green-700 bg-green-100 rounded px-2 py-1">
            Calculated claim amount
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
          <p className="text-xs text-blue-700 font-medium mb-1">AGGRESSIVE</p>
          <p className="text-sm text-blue-800 mb-2 font-semibold">Best Case Scenario</p>
          <p className="text-2xl font-bold text-blue-900 mb-2">
            {formatCurrency(finalClaimAmount * 1.15)}
          </p>
          <div className="text-xs text-blue-700 bg-blue-100 rounded px-2 py-1">
            15% increase in assumptions
          </div>
        </div>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="font-semibold text-purple-900 mb-3 flex items-center">
          <span className="mr-2">🎯</span>
          Key Variables Impact Analysis
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-purple-800">Revenue Drop ±5%:</span>
            <span className="font-mono font-semibold text-purple-900">
              {formatCurrency(finalClaimAmount * 0.95)} - {formatCurrency(finalClaimAmount * 1.05)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-purple-800">Indemnity Period ±30 days:</span>
            <span className="font-mono font-semibold text-purple-900">
              {formatCurrency(finalClaimAmount * 0.90)} - {formatCurrency(finalClaimAmount * 1.10)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-purple-800">Variable Margin ±2%:</span>
            <span className="font-mono font-semibold text-purple-900">
              {formatCurrency(finalClaimAmount * 0.97)} - {formatCurrency(finalClaimAmount * 1.03)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <span className="font-semibold">For Settlement Negotiations:</span> The claim range of{' '}
          <span className="font-bold">{formatCurrency(finalClaimAmount * 0.85)}</span> to{' '}
          <span className="font-bold">{formatCurrency(finalClaimAmount * 1.15)}</span>{' '}
          provides a defensible negotiation window, with the base case of{' '}
          <span className="font-bold">{formatCurrency(finalClaimAmount)}</span> representing the most likely outcome.
        </p>
      </div>
    </div>
  );
}
